import './ProjectListImportDataTable.scss';
import clsx from 'clsx';
import { fold, isLeft, isRight } from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'src/modules/common/components/Checkbox';
import { FormattedMathMessage } from 'src/modules/common/components/FormattedMathMessage';
import { Tag } from 'src/modules/common/components/Tag';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { InfoIcon } from 'src/modules/common/icons/InfoIcon';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { BodyMedium02 } from 'src/modules/common/typography/BodyMedium02';
import { ProjectListImportData, ProjectListImportEntry } from 'src/modules/project-list/types/ProjectListImport';

type Props = {
  readonly content: ProjectListImportData;
  readonly selected: ReadonlyArray<ProjectListImportEntry>;
  readonly onChange: (selected: ReadonlyArray<ProjectListImportEntry>) => void;
};

export const ProjectListImportDataTable = ({ content, selected, onChange }: Props): React.ReactElement => {
  const [activeTab, setActiveTab] = useState<'all' | 'errors'>('all');

  const errorCount = content.reduce((sum, it) => (isRight(it.importData) ? sum : sum + 1), 0);
  const visibleProjects = activeTab === 'all' ? content : content.filter((it) => isLeft(it.importData));
  const validProjects = content.map((it) => it.importData).filter(isRight).map((it) => it.right);
  const allValidSelected = selected.length === validProjects.length;
  const someValidSelected = selected.length > 0;

  const handleHeaderCheckboxChange = (): void => {
    if (allValidSelected) {
      return onChange([]);
    }

    return onChange(validProjects);
  };

  const handleToggle = useCallback((entry: ProjectListImportEntry) => {
    if (selected.includes(entry)) {
      onChange(selected.filter((it) => it !== entry));
    } else {
      onChange(selected.concat(entry));
    }
  }, [selected, onChange]);

  const handleErrorTabClick = (): void => {
    if (errorCount > 0) {
      setActiveTab('errors');
    }
  };

  return (
    <div className="bp-project-list-import-data-table">
      <div className="bp-project-list-import-data-table__tabs">
        <button
          type="button"
          className={clsx({
            'bp-project-list-import-data-table__tab': true,
            'bp-project-list-import-data-table__tab--selected': activeTab === 'all',
          })}
          onClick={() => setActiveTab('all')}
        >
          <BodyMedium02>
            <FormattedMessage id="projectList/importModal/selector/tab/all" values={{ length: content.length }}/>
          </BodyMedium02>
        </button>

        <button
          type="button"
          className={clsx({
            'bp-project-list-import-data-table__tab': true,
            'bp-project-list-import-data-table__tab--selected': activeTab === 'errors',
            'bp-project-list-import-data-table__tab--disabled': errorCount === 0,
          })}
          onClick={handleErrorTabClick}
        >
          <BodyMedium02>
            <FormattedMessage id="projectList/importModal/selector/tab/errors" values={{ errors: errorCount }}/>
          </BodyMedium02>
        </button>
      </div>

      {visibleProjects.length > 0 && (
        <div className="bp-project-list-import-data-table__table">
          <div
            className={clsx({
              'bp-project-list-import-data-table__cell': true,
              'bp-project-list-import-data-table__cell--checkbox': true,
              'bp-project-list-import-data-table__cell--header': true,
            })}
          >
            {activeTab === 'all' && (
              <Checkbox
                id="bp-project-list-import-data-table-header-checkbox"
                onChange={handleHeaderCheckboxChange}
                checked={allValidSelected}
                partial={someValidSelected}
              />
            )}
          </div>

          <div
            className={clsx({
              'bp-project-list-import-data-table__cell': true,
              'bp-project-list-import-data-table__cell--header': true,
            })}
          >
            <BodyMedium01>
              <FormattedMessage id="projectList/importModal/selector/header/status"/>
            </BodyMedium01>
          </div>

          <div
            className={clsx({
              'bp-project-list-import-data-table__cell': true,
              'bp-project-list-import-data-table__cell--header': true,
            })}
          >
            <BodyMedium01>
              <FormattedMessage id="projectList/importModal/selector/header/project"/>
            </BodyMedium01>
          </div>

          <div
            className={clsx({
              'bp-project-list-import-data-table__cell': true,
              'bp-project-list-import-data-table__cell--header': true,
              'bp-project-list-import-data-table__cell--row': true,
            })}
          >
            <BodyMedium01>
              <FormattedMessage id="projectList/importModal/selector/header/row"/>
            </BodyMedium01>
          </div>

          {visibleProjects.map((row) => (
            <React.Fragment key={row.rowNumber}>
              <div
                className="bp-project-list-import-data-table__cell bp-project-list-import-data-table__cell--checkbox"
              >
                {pipe(row.importData, fold((importErrors) => (
                  <Tooltip
                    id={`import-error-details--${row.rowNumber}`}
                    place="right"
                    value={(
                      <ol>
                        {importErrors.map((importError, errorIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                          <li key={errorIndex}>
                            <p className="bp-project-list-import-data-table__error-text">
                              <FormattedMathMessage
                                id="projectList/importModal/selector/item/invalidDetails"
                                values={{ ...importError, code }}
                              />
                            </p>
                          </li>
                        ))}
                      </ol>
                    )}
                  >
                    <div className="bp-project-list-import-data-table__cell-icon">
                      <InfoIcon/>
                    </div>
                  </Tooltip>
                ), (importEntry) => (
                  <Checkbox
                    id={String(row.rowNumber)}
                    onChange={() => handleToggle(importEntry)}
                    checked={selected.includes(importEntry)}
                  />
                )))}
              </div>

              <div className="bp-project-list-import-data-table__cell">
                {pipe(row.importData, fold(() => (
                  <Tag
                    status="error"
                  >
                    <FormattedMessage id="projectList/importModal/selector/item/statusFailure"/>
                  </Tag>
                ), () => (
                  <Tag
                    status="success"
                  >
                    <FormattedMessage id="projectList/importModal/selector/item/statusSuccess"/>
                  </Tag>
                )))}
              </div>

              <div className="bp-project-list-import-data-table__cell">
                {pipe(row.importData, fold(() => (
                  <p className="bp-project-list-import-data-table__invalid-text">
                    <FormattedMessage id="projectList/importModal/selector/item/invalidData"/>
                  </p>
                ), (importEntry) => (
                  <p className="bp-project-list-import-data-table__valid-text">
                    {importEntry.project.title}
                  </p>
                )))}
              </div>

              <div className="bp-project-list-import-data-table__cell bp-project-list-import-data-table__cell--row">
                <p
                  className="bp-project-list-import-data-table__row-text
                        bp-project-list-import-data-table__row-text--number"
                >
                  <FormattedMessage
                    id="projectList/importModal/selector/item/rowNumber"
                    values={{ number: row.rowNumber }}
                  />
                </p>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {visibleProjects.length === 0 && (
        <p className="bp-project-list-import-data-table__empty">
          <FormattedMessage id="projectList/importModal/selector/empty"/>
        </p>
      )}
    </div>
  );
};

const code = (children: React.ReactNode): React.ReactElement => (
  <b style={{ fontFamily: 'monospace' }}>{children}</b>
);
